import * as React from "react";
import { DatabaseTabKey, DatabaseTabs } from "./DatabaseTabs";
import { AudienceFilter } from "./AudienceFilter";
import { useLocationConfiguration } from "./LocationConfiguration";
import { Divider, Paper, Stack, Box } from "@mui/material";
import { TLCEvents } from "../events/events";
import { LS2PAC_CONSTANTS } from "../../main/constants";
import { LS2PacSearchDatabaseManager } from "@tlc/ls2pac/app/managers/SearchDatabaseManager";
import { TLCEventManager } from "../events/EventManager";
import { TLC_CONSTANTS } from "../tlc-constants";
import { ReservesResultsFilter } from "./ReservesResultsFilter";
import { BranchLimitControl } from "./BranchLimitControl";
import { LoginClientManager } from "@tlc/ls2pac/app/ClientManagers/LoginClientManager";
import { ApplicationData } from "@tlc/ls2pac/app/ApplicationData";
import { useBranchLimits } from "./BranchLimits";
import { FacetViewWrapper } from "./FacetViewWrapper";
import { WorldbookViewWrapper } from "./WorldbookViewWrapper";
import { useWindowLocation } from "./WindowLocation";

export const SearchResultSidebar: React.FC = () => {
  const { replaceHashParam } = useWindowLocation();
  const { locationConfiguration: { pacSettings } = { pacSettings: null } } =
    useLocationConfiguration();
    const {displayBranchLimitControl, branchLimits} = useBranchLimits();

  const [ebscoCount, setEbscoCount] = React.useState<number | null>(null);
  const [galeCount, setGaleCount] = React.useState<number | null>(null);
  const [britannicaCount, setBritannicaCount] = React.useState<number | null>(
    null
  );
  const [newsBankCount, setNewsBankCount] = React.useState<number | null>(null);
  const [worldBookCount, setWorldBookCount] = React.useState<number | null>(
    null
  );
  const [reservesCount, setReservesCount] = React.useState<number | null>(null);
  const [pacCount, setPacCount] = React.useState<number | null>(null);

  const [displayAudience, setDisplayAudience] = React.useState(false);
  const [audience, setAudience] = React.useState(null);

  const [displayReservesFilter, setDisplayReservesFilter] =
    React.useState(false);
  const [db, setDB] = React.useState<DatabaseTabKey>(DatabaseTabKey.ls2pac);

  const [displayWorldBook, setDisplayWorldBook] = React.useState(false);

  const [displayFacetsview, setdisplayFacetsview] = React.useState(false);

  const handleAudienceSearchParam = (audienceSearchParam: string) => {
    if (!audienceSearchParam) {
      setAudience("");
      return;
    }

    try {
      const audience = JSON.parse(audienceSearchParam);
      if (audience.length === 0) {
        setAudience("");
        return;
      }

      if (audience[0] === "Adult / General") {
        setAudience("Adult / General");
        return;
      }

      if (audience[0] === "Juvenile") {
        setAudience("Juvenile");
        return;
      }

      setAudience("");
    } catch (e) {
      console.error("Error parsing audience param", e);
      setAudience("");
    }
  };

  const handleDBSearchParam = (dbSearchParam: string) => {
    if (dbSearchParam && Object.keys(DatabaseTabKey).includes(dbSearchParam)) {
      setDB(dbSearchParam as DatabaseTabKey);
    }
  };

  React.useEffect(() => {
    if (pacSettings && db) {
      setDisplayAudience(
        db === DatabaseTabKey.ls2pac &&
          pacSettings.displaySettings.displayAudienceCharacteristicsFilter
      );
      setDisplayReservesFilter(
        db === DatabaseTabKey.reserves && pacSettings.reservesEnabled
      );
      const enabledWorldBookOptions = pacSettings.worldBookDbOptions.filter((option) => option.useThis == true).length;
      setDisplayWorldBook(
        db === DatabaseTabKey.worldbook && pacSettings.useWorldBook && enabledWorldBookOptions > 1
      );
      
    }
  }, [pacSettings, db]);

  // get all the initial values from the hash
  React.useEffect(() => {
    const handleHashChange = () => {
      const urlSearchParams = new URLSearchParams(
        window.location.hash.slice(1)
      );

      const section = urlSearchParams.get("section");
      if (section === "search") {
        handleDBSearchParam(urlSearchParams.get("db"));
        handleAudienceSearchParam(urlSearchParams.get("audience"));
      }
    };
    const handlePagingModelResponse = ({
      success,
      data,
    }: {
      success: boolean;
      data: { totalHits: number; databaseKey: DatabaseTabKey };
    }) => {
      handleDatabaseSearchCountResponse({
        success,
        data,
        databaseKey: data.databaseKey,
      });
    };

    const handleDatabaseSearchCountResponse = ({
      databaseKey,
      data: { totalHits },
    }: {
      success: boolean;
      data: { totalHits: number };
      databaseKey: DatabaseTabKey;
    }) => {
      switch (databaseKey) {
        case DatabaseTabKey.britannica:
          setBritannicaCount(totalHits);
          break;
        case DatabaseTabKey.ebsco:
          setEbscoCount(totalHits);
          break;
        case DatabaseTabKey.newsbank:
          setNewsBankCount(totalHits);
          break;
        case DatabaseTabKey.gale:
          setGaleCount(totalHits);
          break;
        case DatabaseTabKey.ls2pac:
          setPacCount(totalHits);
          break;
        case DatabaseTabKey.reserves:
          setReservesCount(totalHits);
          break;
        case DatabaseTabKey.worldbook:
          setWorldBookCount(totalHits);
          break;
      }
    };
    const handleLoading = () => {
      setBritannicaCount(null);
      setPacCount(null);
      setEbscoCount(null);
      setReservesCount(null);
      setGaleCount(null);
      setNewsBankCount(null);
      setWorldBookCount(null);
    };
    const resetDatabase = () => {
      setDB(DatabaseTabKey.ls2pac);
      LS2PacSearchDatabaseManager.setCurrentDatabaseKey(DatabaseTabKey.ls2pac);
    };

    handleHashChange();

    const removeListenerFunctions: Array<() => void> = [];
    const addListener = (eventString: string, callback: Function) => {
      const guid = TLCEventManager.addListener(eventString, callback);
      removeListenerFunctions.push(() => TLCEventManager.removeListener(eventString, guid));
    }

    const removeAllListeners = () => {
      removeListenerFunctions.forEach(remove => remove());
      removeListenerFunctions.length = 0;
    }

    addListener(
      TLC_CONSTANTS.Events.onPagingModelResponse, 
      handlePagingModelResponse
    );
    addListener(
      LS2PAC_CONSTANTS.Events.onGetDatabaseSearchCount,
      handleDatabaseSearchCountResponse
    );
    addListener(
      LS2PAC_CONSTANTS.Events.onSearch,
      handleLoading
    );
    addListener(
      LS2PAC_CONSTANTS.Events.onFireAdvancedSearch,
      handleLoading
    );
    addListener(LS2PAC_CONSTANTS.Events.onHome, resetDatabase);
    addListener(
      LS2PAC_CONSTANTS.Events.onLogoutResponse,
      resetDatabase
    );


    
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      removeAllListeners();
      window.removeEventListener("hashchange", handleHashChange);
    };

  }, []);


  const handleAudienceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    const audience = value ? [value] : [];
    TLCEvents.trigger(LS2PAC_CONSTANTS.Events.onSetAudience, {
      audience,
      doSearch: true,
    });
  };

  const databaseRequiresLogin = (db: DatabaseTabKey) => {
    // TODO move to a context
    if (ApplicationData.locationConfiguration.isStationLocationSet()) {
      return false;
    }
    return db !== DatabaseTabKey.ls2pac && db !== DatabaseTabKey.reserves;
  };

  const updateDatabase = (value: DatabaseTabKey) => {
    setDB(value);
    LS2PacSearchDatabaseManager.setCurrentDatabaseKey(value);
    TLCEventManager.fireEvent(LS2PAC_CONSTANTS.Events.onSwitchSearchDatabase, {
      databaseKey: value,
      onChangeViewEvent: true,
    });
    //Hide Show Facets view 
    const hasFacets = LS2PacSearchDatabaseManager.getCurrentDatabaseConfig().hasFacets
    console.log("hasfacets: ", hasFacets);
    if (!hasFacets)
    {
      setdisplayFacetsview(false);
    } else {
      setdisplayFacetsview(true);
    }
    
  };

  const handleDatabaseTabChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: DatabaseTabKey
  ) => {
    if (databaseRequiresLogin(value)) {
      // TODO investigate why promptIfNotLoggedIn does not fire callback after login
      LoginClientManager.promptIfNotLoggedIn(() => {
        updateDatabase(value);
      });
    } else {
      updateDatabase(value);
    } 
  };

  return (
    <React.Fragment>
      <Paper sx={{ minHeight: "100vh", height: "100%"}}>
        <Stack direction="column" gap={2} sx={{pt: 1, pb: 1}}>
            {db && (
              <Box sx={{ pt: 0, pb: 0 }}>
                <DatabaseTabs
                  value={db}
                  onChange={handleDatabaseTabChange}
                  useBritannica={pacSettings?.useBritannica}
                  useGale={pacSettings?.useGale}
                  useEbsco={pacSettings?.useEbsco}
                  useNewsBank={pacSettings?.useNewsBank}
                  useWorldBook={pacSettings?.useWorldBook}
                  reservesEnabled={pacSettings?.reservesEnabled}
                  pacCount={pacCount}
                  ebscoCount={ebscoCount}
                  galeCount={galeCount}
                  britannicaCount={britannicaCount}
                  newsBankCount={newsBankCount}
                  worldBookCount={worldBookCount}
                  reservesCount={reservesCount}
                ></DatabaseTabs>
              </Box>
            )}
            <Divider orientation="horizontal" variant="fullWidth" aria-hidden="true"/>
          {displayReservesFilter && (
            <Box sx={{p: 2}}><ReservesResultsFilter></ReservesResultsFilter></Box>  
          )}
          
          {displayBranchLimitControl && <Box sx={{ pl: 2 }}>
            <BranchLimitControl
              onChangeBranchLimits={(newBranchIdentifiers) => {
                const url = new URL(window.location.href);
                const hashParams = new URLSearchParams(url.hash.slice(1));
                hashParams.set("page", "0");
                hashParams.set("branchFilters", JSON.stringify(newBranchIdentifiers));
                window.location.hash = hashParams.toString();
                // replaceHashParam("branchFilters", JSON.stringify(newBranchIdentifiers));
              }}
            ></BranchLimitControl>
          </Box>}
          <Divider orientation="horizontal" variant="fullWidth" aria-hidden="true"/>
          {displayAudience && (
              <AudienceFilter
                value={audience}
                onChange={handleAudienceChange}
                sx={{
                  p: 2,
                }}
              ></AudienceFilter>
          )}

          {/* DRILL the props for sx   */}
          <FacetViewWrapper 
            sx={{
              p: 2
            }}
          />
          
          { displayWorldBook  && (
            <WorldbookViewWrapper 
              sx={{
                p: 2
              }}
            />
          )}
      
        </Stack>
      </Paper>
    </React.Fragment>
  );
};
